import { cva } from "class-variance-authority";
import {
  DialogContent,
  DialogDescription,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
} from "reka-ui";
import { defineComponent } from "vue";
import ScrollArea from "@/components/ui/scroll-area/ScrollArea";
import { cn } from "@/utils/cn";
import { p } from "@/utils/props-builder";
import Dialog from "../dialog/Dialog";

// ---------------
// Root
// ---------------
const Root = Dialog.Root;

// ---------------
// Trigger
// ---------------
const Trigger = defineComponent({
  name: "SheetTrigger",
  props: {
    asChild: p.boolean(),
    as: p.string<keyof HTMLElementTagNameMap>("button"),
  },
  setup(props, { slots }) {
    return () => <DialogTrigger {...props}>{slots.default?.()}</DialogTrigger>;
  },
});

// ---------------
// Content
// ---------------
const sheetVariants = cva(
  "fixed z-50 flex flex-col gap-4 bg-background text-foreground shadow-lg transition ease-in-out data-[state=closed]:duration-300 data-[state=open]:duration-500 data-[state=open]:animate-in data-[state=closed]:animate-out",
  {
    variants: {
      side: {
        top: "inset-x-0 top-0 border-b data-[state=closed]:slide-out-to-top data-[state=open]:slide-in-from-top",
        bottom:
          "inset-x-0 bottom-0 border-t data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom",
        left: "inset-y-0 left-0 h-full w-[calc(100vw-3rem)] border-r data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left",
        right:
          "inset-y-0 right-0 h-full w-3/4 border-l data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right",
      },
      size: {
        sm: "",
        md: "",
        lg: "",
      },
    },
    compoundVariants: [
      // Right side variants
      { side: "right", size: "sm", class: "sm:max-w-sm" },
      { side: "right", size: "md", class: "sm:max-w-md lg:max-w-lg" },
      { side: "right", size: "lg", class: "sm:max-w-xl lg:max-w-2xl" },
      // Left side variants
      { side: "left", size: "sm", class: "max-w-sm" },
      { side: "left", size: "md", class: "max-w-md lg:max-w-lg" },
      { side: "left", size: "lg", class: "max-w-xl lg:max-w-2xl" },
      // Top side variants
      { side: "top", size: "sm", class: "max-h-[40vh]" },
      { side: "top", size: "md", class: "max-h-[60vh]" },
      { side: "top", size: "lg", class: "max-h-[80vh]" },
      // Bottom side variants
      { side: "bottom", size: "sm", class: "max-h-[40vh]" },
      { side: "bottom", size: "md", class: "max-h-[60vh]" },
      { side: "bottom", size: "lg", class: "max-h-[80vh]" },
    ],
    defaultVariants: {
      side: "right",
      size: "sm",
    },
  }
);

const Content = defineComponent({
  name: "SheetContent",
  emits: {
    closeAutoFocus: (_e: Event) => true,
    dismiss: () => true,
    escapeKeyDown: (_e: Event) => true,
    focusOutside: (_e: Event) => true,
    interactOutside: (_e: Event) => true,
    openAutoFocus: (_e: Event) => true,
    pointerDownOutside: (_e: Event) => true,
  },
  props: {
    side: p.string<"top" | "bottom" | "left" | "right">(),
    size: p.string<"sm" | "md" | "lg">("sm"),
    asChild: p.boolean(),
    as: p.string<keyof HTMLElementTagNameMap>(),
    class: p.string(),
    disableOutsidePointerEvents: p.boolean(false),
    forceMount: p.boolean(false),
    trapFocus: p.boolean(true),
  },
  setup(props, { slots, emit }) {
    return () => (
      <DialogPortal>
        <DialogOverlay class="fixed inset-0 z-50 bg-background/80 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 dark:bg-background/80" />
        <DialogContent
          {...props}
          onCloseAutoFocus={props.asChild ? undefined : (e: Event) => emit("closeAutoFocus", e)}
          // onDismiss={props.asChild ? undefined : () => emit("dismiss")}
          onEscapeKeyDown={props.asChild ? undefined : (e: Event) => emit("escapeKeyDown", e)}
          onFocusOutside={props.asChild ? undefined : (e: Event) => emit("focusOutside", e)}
          onInteractOutside={props.asChild ? undefined : (e: Event) => emit("interactOutside", e)}
          onOpenAutoFocus={props.asChild ? undefined : (e: Event) => emit("openAutoFocus", e)}
          onPointerDownOutside={
            props.asChild ? undefined : (e: Event) => emit("pointerDownOutside", e)
          }
          class={cn(sheetVariants({ side: props.side, size: props.size }), props.class)}
        >
          <ScrollArea class="p-0">{slots.default?.()}</ScrollArea>

          {/* <DialogClose class="hover:bg-secondary text-foreground absolute right-4 top-4 rounded-md p-0.5 transition-colors">
            <Icon icon="lucide:x" class="text-muted-foreground h-4 w-4" />
          </DialogClose> */}
        </DialogContent>
      </DialogPortal>
    );
  },
});

const Header = defineComponent({
  name: "SheetHeader",
  props: {
    class: p.string(),
  },
  setup(props, { slots }) {
    return () => (
      <div class={cn("flex flex-col space-y-2 text-center sm:text-left", props.class)}>
        {slots.default?.()}
      </div>
    );
  },
});

const Title = defineComponent({
  name: "SheetTitle",
  props: {
    class: p.string(),
    as: p.string<keyof HTMLElementTagNameMap>(),
    asChild: p.boolean(),
  },
  setup(props, { slots }) {
    return () => (
      <DialogTitle {...props} class={cn("text-lg font-semibold text-foreground", props.class)}>
        {slots.default?.()}
      </DialogTitle>
    );
  },
});

const Description = defineComponent({
  name: "SheetDescription",
  props: {
    class: p.string(),
    as: p.string<keyof HTMLElementTagNameMap>(),
    asChild: p.boolean(),
  },
  setup(props, { slots }) {
    return () => (
      <DialogDescription {...props} class={cn("text-sm text-muted-foreground", props.class)}>
        {slots.default?.()}
      </DialogDescription>
    );
  },
});

const Footer = defineComponent({
  name: "SheetFooter",
  props: {
    class: p.string(),
  },
  setup(props, { slots }) {
    return () => (
      <div class={cn("flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2", props.class)}>
        {slots.default?.()}
      </div>
    );
  },
});

const Body = defineComponent({
  name: "SheetBody",
  props: {
    class: p.string(),
  },
  setup(props, { slots }) {
    return () => <div class={cn("flex-1 py-6", props.class)}>{slots.default?.()}</div>;
  },
});

export default {
  Root,
  Trigger,
  Content,
  Header,
  Title,
  Description,
  Footer,
  Body,
};
